import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';

const WHATSAPP_BUNDLE = 'https://wa.me/18495388899?text=Hola%20InnoFlex!%20Me%20gustar%C3%ADa%20el%20Paquete%20Completo%20(Sistema%20%2B%20Certificaci%C3%B3n%20e-CF%20%2B%20Portal%20RepublicaFEL)';
const FEL_WEBSITE = 'https://republicafel.com/';

function RepublicaFELLogo({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const dims = size === 'lg' ? 'h-14' : size === 'md' ? 'h-10' : 'h-7';
  return (
    <img
      src="https://republicafel.com/wp-content/uploads/2023/03/Logo-RepublicaFEL-1.png"
      alt="RepublicaFEL logo"
      className={`${dims} w-auto object-contain`}
      onError={(e) => {
        const target = e.currentTarget;
        target.style.display = 'none';
        const fallback = target.nextElementSibling as HTMLElement | null;
        if (fallback) fallback.style.display = 'flex';
      }}
    />
  );
}

function RepublicaFELLogoBadge() {
  return (
    <div className="flex items-center gap-2">
      <RepublicaFELLogo size="md" />
      <div className="items-center gap-2 hidden" style={{ display: 'none' }}>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: '#e53535' }}>
          <i className="ri-file-list-3-line text-white text-sm"></i>
        </div>
        <span className="font-heading font-extrabold text-lg" style={{ color: '#e53535' }}>
          República<span style={{ color: '#c41e1e' }}>FEL</span>
        </span>
      </div>
    </div>
  );
}

export default function Partners() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [hovered, setHovered] = useState(false);

  const FEL_RED = '#e53535';

  const tags = [
    { key: 'partners_fel_tag1', icon: 'ri-shield-check-line' },
    { key: 'partners_fel_tag2', icon: 'ri-global-line' },
    { key: 'partners_fel_tag3', icon: 'ri-plug-line' },
    { key: 'partners_fel_tag4', icon: 'ri-customer-service-2-line' },
  ];

  const addons = [
    { labelKey: 'partners_addon_impl', priceKey: 'partners_addon_impl_price', noteKey: 'partners_addon_impl_note', accent: '#00cfff', icon: 'ri-settings-3-line' },
    { labelKey: 'partners_addon_fel',  priceKey: 'partners_addon_fel_price',  noteKey: 'partners_addon_fel_note',  accent: FEL_RED,   icon: 'ri-file-text-line' },
  ];

  // e-CF certification cost across the three routes (price clarity)
  const routes = [
    {
      id: 'direct',
      labelKey: 'fel_route1_title',
      subKey:   'fel_route1_sub',
      price:    '$450',
      noteKey:  'fel_route1_note',
      accent:   isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.45)',
      icon:     'ri-external-link-line',
    },
    {
      id: 'fel',
      labelKey:   'fel_route2_title',
      subKey:     'fel_route2_sub',
      price:      '$300',
      priceSubKey: null as string | null,
      savingsKey: 'fel_comp_save_150',
      noteKey:    'fel_route2_note',
      accent:     '#00cfff',
      icon:       'ri-file-text-line',
    },
    {
      id: 'bundle',
      labelKey:    'fel_route3_title',
      subKey:      'fel_route3_sub',
      price:       '$250 + $200',
      priceSubKey: 'fel_route3_price_sub',
      noteKey:     'fel_route3_note',
      accent:      '#facc15',
      icon:        'ri-sparkling-2-line',
      best:        true,
    },
  ] as Array<{
    id: string; labelKey: string; subKey: string; price: string;
    priceSubKey?: string | null; savingsKey?: string; noteKey: string;
    accent: string; icon: string; best?: boolean;
  }>;

  const cardBorderColor = hovered
    ? FEL_RED + '70'
    : isLight ? FEL_RED + '35' : FEL_RED + '25';

  const cardBg = isLight
    ? hovered ? `linear-gradient(135deg, ${FEL_RED}08 0%, #ffffff 60%)` : '#ffffff'
    : hovered ? `linear-gradient(135deg, ${FEL_RED}12 0%, rgba(6,15,35,0.98) 70%)` : 'rgba(12,22,48,0.95)';

  return (
    <section
      id="partners"
      className="py-14 md:py-24 px-4 sm:px-5 md:px-6"
      style={{ backgroundColor: isLight ? '#f8fbff' : 'rgba(4,10,24,1)' }}
    >
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span
            className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3"
            style={{ color: FEL_RED }}
          >
            {t('partners_label')}
          </span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>
            {t('partners_title_1')} <span style={{ color: FEL_RED }}>{t('partners_title_accent')}</span>
          </h2>
          <p className="font-body text-sm md:text-base max-w-xl mx-auto" style={{ color: isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.55)' }}>
            {t('partners_sub')}
          </p>
        </div>

        {/* Main Partner Card */}
        <div
          className="relative rounded-2xl border overflow-hidden transition-all duration-300 cursor-default"
          style={{ borderColor: cardBorderColor, background: cardBg }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="h-[3px] w-full" style={{ background: `linear-gradient(to right, ${FEL_RED}, ${FEL_RED}40, transparent)` }} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left: Partner Info */}
            <div className="p-6 md:p-10 flex flex-col">
              <div className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full mb-6 border" style={{ backgroundColor: FEL_RED + '14', borderColor: FEL_RED + '40', color: FEL_RED }}>
                <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-verified-badge-line text-xs"></i>
                </div>
                <span className="font-body text-[11px] font-bold tracking-wide">{t('partners_fel_category')}</span>
              </div>

              <div className="mb-5"><RepublicaFELLogoBadge /></div>

              <p className="font-heading text-xl md:text-2xl font-bold mb-4 leading-snug" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>
                {t('partners_fel_tagline')}
              </p>

              <p className="font-body text-sm leading-relaxed mb-6" style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.60)' }}>
                {t('partners_fel_desc')}
              </p>

              <div className="flex flex-wrap gap-2 mb-8">
                {tags.map((tag) => (
                  <span
                    key={tag.key}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs font-semibold"
                    style={{ backgroundColor: FEL_RED + '12', border: `1px solid ${FEL_RED}30`, color: isLight ? FEL_RED : '#ff7676' }}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0">
                      <i className={`${tag.icon} text-xs`}></i>
                    </div>
                    {t(tag.key)}
                  </span>
                ))}
              </div>

              {/* CTA Links */}
              <div className="flex flex-wrap items-center gap-3 mt-auto">
                <a
                  href={FEL_WEBSITE}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
                  style={{ backgroundColor: FEL_RED, color: '#ffffff', boxShadow: `0 8px 24px ${FEL_RED}45` }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </div>
                  {t('partners_fel_cta')}
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Bundle Offer */}
            <div
              className="p-6 md:p-10 flex flex-col justify-between border-t lg:border-t-0 lg:border-l"
              style={{ borderColor: isLight ? FEL_RED + '20' : FEL_RED + '18', background: isLight ? `linear-gradient(135deg, ${FEL_RED}06 0%, rgba(248,251,255,0.9) 100%)` : `linear-gradient(135deg, ${FEL_RED}10 0%, rgba(4,10,24,0.4) 100%)` }}
            >
              <div>
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: '#facc1518', color: '#facc15' }}>
                    <i className="ri-sparkling-2-line text-sm"></i>
                  </div>
                  <span className="font-body text-xs font-bold tracking-widest uppercase" style={{ color: '#facc15' }}>
                    {t('partners_bundle_label')}
                  </span>
                </div>

                <p className="font-body text-sm leading-relaxed mb-6" style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.58)' }}>
                  {t('partners_bundle_desc')}
                </p>

                <div className="space-y-3 mb-6">
                  {addons.map((a) => (
                    <div key={a.labelKey} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: isLight ? a.accent + '08' : a.accent + '10', border: `1px solid ${a.accent}25` }}>
                      <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: a.accent + '18', color: a.accent }}>
                        <i className={`${a.icon} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-body text-xs font-semibold" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>{t(a.labelKey)}</span>
                        <span className="font-body text-[10px] block" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>{t(a.noteKey)}</span>
                      </div>
                      <span className="font-heading text-sm font-bold flex-shrink-0" style={{ color: a.accent }}>{t(a.priceKey)}</span>
                    </div>
                  ))}

                  <div className="flex items-center justify-center gap-3">
                    <div className="flex-1 h-px" style={{ background: isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.08)' }} />
                    <span className="font-body text-xs font-bold" style={{ color: isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.30)' }}>+</span>
                    <div className="flex-1 h-px" style={{ background: isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.08)' }} />
                  </div>

                  <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: FEL_RED + '10', border: `1px solid ${FEL_RED}30` }}>
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: FEL_RED + '18', color: FEL_RED }}>
                      <i className="ri-global-line text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-body text-xs font-semibold" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>Portal RepublicaFEL</span>
                      <span className="font-body text-[10px] block" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>Plataforma certificada DGII incluida</span>
                    </div>
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#facc15' }}>
                      <i className="ri-check-line text-xs font-bold"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-end gap-2 mb-1">
                  <span className="font-heading text-4xl font-extrabold" style={{ color: '#facc15' }}>$450</span>
                  <span className="font-body text-sm mb-1.5" style={{ color: isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.45)' }}>único pago</span>
                </div>
                <p className="font-body text-xs mb-5" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>
                  {t('partners_bundle_note')}
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0" style={{ color: '#facc15' }}>
                    <i className="ri-price-tag-3-line text-xs"></i>
                  </div>
                  <span className="font-body text-xs font-semibold" style={{ color: '#facc15' }}>Ahorras $200 vs. contratar todo por separado</span>
                </div>
                <a
                  href={WHATSAPP_BUNDLE}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 font-bold text-sm py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap font-body hover:scale-[1.02] active:scale-[0.98] hover:opacity-90"
                  style={{ background: 'linear-gradient(to right, #facc15, #f5b800)', color: '#0a1628' }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-sm"></i>
                  </div>
                  {t('partners_bundle_cta')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── e-CF Certification: cost by route (price clarity + partner benefits) ── */}
        <div className="mt-10 md:mt-12">
          <div className="text-center mb-6">
            <span className="inline-block text-xs md:text-sm font-semibold tracking-widest font-body mb-2" style={{ color: FEL_RED }}>
              {t('fel_routes_label')}
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-bold mb-1.5" style={{ color: isLight ? '#0a1628' : '#ffffff' }}>
              {t('fel_routes_title')}
            </h3>
            <p className="font-body text-sm max-w-xl mx-auto" style={{ color: isLight ? 'rgba(10,22,40,0.55)' : 'rgba(255,255,255,0.50)' }}>
              {t('fel_routes_sub')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {routes.map((r) => (
              <div
                key={r.id}
                className="relative rounded-2xl border p-5 flex flex-col overflow-hidden"
                style={{
                  borderColor: r.best ? r.accent + '70' : (isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.10)'),
                  background: r.best
                    ? (isLight ? `linear-gradient(135deg, ${r.accent}14 0%, #ffffff 70%)` : `linear-gradient(135deg, ${r.accent}12 0%, rgba(12,22,48,0.95) 70%)`)
                    : (isLight ? '#ffffff' : 'rgba(12,22,48,0.85)'),
                }}
              >
                {r.best && <div className="absolute top-0 left-0 right-0 h-[2.5px]" style={{ background: r.accent }} />}

                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: r.accent + '18', color: r.accent }}>
                    <i className={`${r.icon} text-base`}></i>
                  </div>
                  {r.best && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full font-body text-[9px] font-extrabold tracking-widest uppercase"
                      style={{ backgroundColor: r.accent + '22', color: r.accent, border: `1px solid ${r.accent}40` }}
                    >
                      {t('fel_comp_best')}
                    </span>
                  )}
                </div>

                <h4 className="font-heading text-sm font-bold" style={{ color: r.best ? r.accent : (isLight ? '#0a1628' : '#ffffff') }}>
                  {t(r.labelKey)}
                </h4>
                <p className="font-body text-[11px] mb-3" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>
                  {t(r.subKey)}
                </p>

                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-heading text-2xl font-extrabold" style={{ color: r.best ? r.accent : (isLight ? '#0a1628' : '#ffffff') }}>
                    {r.price}
                  </span>
                  <span className="font-body text-[11px]" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>único pago</span>
                </div>
                {r.priceSubKey && (
                  <span className="font-body text-[10px] mt-0.5" style={{ color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.40)' }}>
                    {t(r.priceSubKey)}
                  </span>
                )}
                {r.savingsKey && (
                  <div className="mt-2">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-body text-[10px] font-extrabold"
                      style={{ backgroundColor: r.accent + '18', color: r.accent, border: `1px solid ${r.accent}30` }}
                    >
                      <i className="ri-arrow-down-line text-[10px]"></i>
                      {t(r.savingsKey)}
                    </span>
                  </div>
                )}

                <p className="font-body text-xs leading-relaxed mt-3 pt-3 border-t" style={{ borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.08)', color: isLight ? 'rgba(10,22,40,0.60)' : 'rgba(255,255,255,0.55)' }}>
                  {t(r.noteKey)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Partnership statement */}
        <div className="mt-8 text-center">
          <p className="font-body text-xs" style={{ color: isLight ? 'rgba(10,22,40,0.35)' : 'rgba(255,255,255,0.28)' }}>
            InnoFlex · Partner Oficial de <strong style={{ color: FEL_RED }}>RepublicaFEL</strong> · Facturación Electrónica Certificada en República Dominicana
          </p>
        </div>

      </div>
    </section>
  );
}
